/**
 * Admin Accuplacer dashboard.
 *
 * Four sub-tabs:
 *   Profiles      — pending registrations awaiting admin to schedule
 *   Sessions      — issued test credentials (active + expired)
 *   Attempts      — submitted tests awaiting proctor review
 *   Completed     — results-sent tests (audit log)
 *
 * Two key dialogs:
 *   StartSessionDialog — admin picks in-person vs online, issues test creds.
 *   ReviewAttemptDialog — full per-question grading view + AI essay score + send-results.
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
    GraduationCap, ClipboardList, ShieldCheck, Mail, Loader2, Copy,
    CheckCircle2, XCircle, Send, Paperclip, KeyRound, Video, MapPin,
    Eye, AlertCircle, Trash2,
} from "lucide-react";

function fmtError(err, fallback = "Action failed.") {
    const d = err?.response?.data?.detail;
    if (!d) return fallback;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || "Invalid input").join("; ");
    return d.msg || JSON.stringify(d);
}

function fmtDate(d) {
    if (!d) return "—";
    try { return new Date(d).toLocaleString(); } catch { return d; }
}

function StatusBadge({ status }) {
    const map = {
        pending_review: ["bg-amber-100 text-amber-800 border-amber-300", "Awaiting scheduling"],
        scheduled: ["bg-sky-100 text-sky-800 border-sky-300", "Scheduled"],
        "in-progress": ["bg-violet-100 text-violet-800 border-violet-300", "In progress"],
        awaiting_review: ["bg-rose-100 text-rose-800 border-rose-300", "Awaiting review"],
        complete: ["bg-emerald-100 text-emerald-800 border-emerald-300", "Complete"],
    };
    const [cls, label] = map[status] || ["bg-muted text-muted-foreground", status];
    return <Badge variant="outline" className={`${cls} text-[10px]`}>{label}</Badge>;
}

function CredentialCell({ value, testid, mono = false }) {
    if (!value) {
        return <span className="text-xs text-muted-foreground">—</span>;
    }
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            toast.success("Copied to clipboard");
        } catch {
            toast.error("Could not copy");
        }
    };
    return (
        <button
            type="button"
            onClick={copy}
            data-testid={testid}
            title="Click to copy"
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-[#E1DAC5] bg-[#FBF5E8]/70 hover:bg-[#FBF5E8] hover:border-[#C79520] transition text-[12px] ${mono ? "font-mono" : "font-mono"} text-[#1A201D]`}
        >
            <span className="truncate max-w-[140px]">{value}</span>
            <Copy className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        </button>
    );
}

export default function AdminAccuplacer() {
    const [stats, setStats] = useState(null);
    const [profiles, setProfiles] = useState([]);
    const [attempts, setAttempts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [startSessionFor, setStartSessionFor] = useState(null); // profile row
    const [reviewing, setReviewing] = useState(null); // attempt_id
    const [viewingProfile, setViewingProfile] = useState(null); // profile row for the read-only details dialog

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [s, p, a] = await Promise.all([
                api.get("/admin/accuplacer/stats"),
                api.get("/admin/accuplacer/profiles"),
                api.get("/admin/accuplacer/attempts"),
            ]);
            setStats(s.data);
            setProfiles(p.data.items || []);
            setAttempts(a.data.items || []);
        } catch (e) {
            toast.error(fmtError(e, "Could not load dashboard."));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    // Bulk-selection state for deleting profiles
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const toggleSelected = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const selectAllVisible = (ids) => {
        setSelectedIds((prev) => {
            const all = ids.every((id) => prev.has(id));
            if (all) return new Set();
            return new Set(ids);
        });
    };
    const bulkDelete = async () => {
        if (selectedIds.size === 0) return;
        setDeleting(true);
        try {
            const { data } = await api.post("/admin/accuplacer/profiles/bulk-delete", {
                profile_ids: Array.from(selectedIds),
            });
            toast.success(`Deleted ${data.deleted} profile(s).`);
            setSelectedIds(new Set());
            setConfirmDeleteOpen(false);
            await reload();
        } catch (e) {
            toast.error(fmtError(e, "Could not delete profiles."));
        } finally {
            setDeleting(false);
        }
    };

    // Show every profile so the admin always has a complete history. The
    // tile counts still reflect status; here, the table is the source of
    // truth and supports bulk-delete to prune old records.
    const pendingProfiles = profiles;
    const awaitingReview = attempts.filter((a) => a.status === "submitted" && !a.results_sent_at);
    const completed = attempts.filter((a) => !!a.results_sent_at);

    return (
        <div className="space-y-6 max-w-7xl mx-auto px-6 py-10 pb-24 w-full min-w-0" data-testid="admin-accuplacer">
            <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C79520] mb-1 flex items-center gap-1.5">
                    <GraduationCap className="h-3 w-3" />
                    Heritage Community College
                </div>
                <h1 className="font-display text-3xl text-[#134B30]">HCC English Test</h1>
                <p className="text-sm text-muted-foreground">
                    English-placement test registrations, sessions, reviews, and results.
                </p>
            </div>

            {/* Tally tiles — never force 5 cols until very wide screens so the
                COMPLETE tile doesn't clip off the right edge with the sidebar open. */}
            <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-5 gap-3 w-full min-w-0">
                {[
                    ["Profiles", stats?.profiles ?? "—", "bg-[#FBF5E8] border-[#E1DAC5]"],
                    ["Active sessions", stats?.active_sessions ?? "—", "bg-sky-50 border-sky-200"],
                    ["In progress", stats?.in_progress ?? "—", "bg-violet-50 border-violet-200"],
                    ["Awaiting review", stats?.awaiting_review ?? "—", "bg-rose-50 border-rose-200"],
                    ["Complete", stats?.complete ?? "—", "bg-emerald-50 border-emerald-200"],
                ].map(([label, n, cls]) => (
                    <Card key={label} className={`p-3 min-w-0 ${cls}`}>
                        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground truncate">{label}</div>
                        <div className="font-display text-2xl mt-1 text-[#134B30]">{n}</div>
                    </Card>
                ))}
            </div>

            <Tabs defaultValue="profiles" className="w-full">
                <TabsList data-testid="accuplacer-admin-tabs">
                    <TabsTrigger value="profiles" data-testid="tab-profiles">
                        <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
                        Profiles ({pendingProfiles.length})
                    </TabsTrigger>
                    <TabsTrigger value="awaiting" data-testid="tab-awaiting">
                        <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
                        Awaiting review ({awaitingReview.length})
                    </TabsTrigger>
                    <TabsTrigger value="completed" data-testid="tab-completed">
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                        Completed ({completed.length})
                    </TabsTrigger>
                </TabsList>

                {/* Profiles */}
                <TabsContent value="profiles" className="mt-4">
                    <Card className="overflow-hidden">
                        {loading ? (
                            <div className="p-6 space-y-3">
                                {[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
                            </div>
                        ) : pendingProfiles.length === 0 ? (
                            <div className="p-12 text-center text-sm text-muted-foreground">
                                <ClipboardList className="h-8 w-8 mx-auto text-[#C79520]/60 mb-2" />
                                No active student profiles.
                            </div>
                        ) : (
                            <div className="overflow-x-auto w-full">
                            {selectedIds.size > 0 ? (
                                <div className="px-4 py-2 bg-rose-50 border-b border-rose-200 flex items-center justify-between text-sm">
                                    <span className="text-rose-800 font-medium">
                                        {selectedIds.size} profile{selectedIds.size === 1 ? "" : "s"} selected
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setSelectedIds(new Set())}
                                            data-testid="bulk-clear-selection"
                                        >
                                            Clear
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="destructive"
                                            onClick={() => setConfirmDeleteOpen(true)}
                                            className="gap-1.5"
                                            data-testid="bulk-delete-btn"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                            Delete selected
                                        </Button>
                                    </div>
                                </div>
                            ) : null}
                            <table className="w-full text-sm" data-testid="profiles-table">
                                <thead className="bg-muted/40 text-xs uppercase tracking-[0.14em] text-muted-foreground">
                                    <tr>
                                        <th className="px-3 py-3 w-10">
                                            <input
                                                type="checkbox"
                                                checked={pendingProfiles.length > 0 && pendingProfiles.every((p) => selectedIds.has(p.id))}
                                                onChange={() => selectAllVisible(pendingProfiles.map((p) => p.id))}
                                                className="h-4 w-4 rounded border-[#C79520] accent-[#134B30] cursor-pointer"
                                                data-testid="select-all-profiles"
                                                aria-label="Select all"
                                            />
                                        </th>
                                        <th className="text-left px-4 py-3 font-semibold">Applicant</th>
                                        <th className="text-left px-4 py-3 font-semibold">Test ID</th>
                                        <th className="text-left px-4 py-3 font-semibold">Password</th>
                                        <th className="text-left px-4 py-3 font-semibold">Status</th>
                                        <th className="text-left px-4 py-3 font-semibold">Submitted</th>
                                        <th className="text-right px-4 py-3 font-semibold">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingProfiles.map((p) => (
                                        <tr key={p.id} className={`border-t border-border ${selectedIds.has(p.id) ? "bg-rose-50/40" : ""}`} data-testid={`profile-row-${p.id}`}>
                                            <td className="px-3 py-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(p.id)}
                                                    onChange={() => toggleSelected(p.id)}
                                                    className="h-4 w-4 rounded border-[#C79520] accent-[#134B30] cursor-pointer"
                                                    data-testid={`select-profile-${p.id}`}
                                                    aria-label={`Select ${p.email}`}
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="font-medium">
                                                    {p.full_name || [p.first_name, p.middle_name, p.last_name].filter(Boolean).join(" ")}
                                                </div>
                                                <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                                                    <Mail className="h-3 w-3" />{p.email}
                                                </div>
                                                {p.student_id ? (
                                                    <div className="text-[11px] text-muted-foreground font-mono">
                                                        ID: {p.student_id}
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td className="px-4 py-3">
                                                <CredentialCell value={p.test_id} testid={`cred-test-id-${p.id}`} />
                                            </td>
                                            <td className="px-4 py-3">
                                                <CredentialCell value={p.test_password} testid={`cred-pwd-${p.id}`} mono />
                                            </td>
                                            <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                                            <td className="px-4 py-3 text-xs">{fmtDate(p.created_at)}</td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="inline-flex items-center gap-1.5">
                                                <button
                                                    type="button"
                                                    onClick={() => setViewingProfile(p)}
                                                    className="inline-flex items-center gap-1 text-[11px] text-[#134B30] border border-[#E1DAC5] bg-[#FBF5E8]/70 hover:bg-[#FBF5E8] hover:border-[#C79520] px-2 py-1.5 rounded-md transition"
                                                    data-testid={`profile-info-${p.id}`}
                                                    title="View student-submitted profile"
                                                >
                                                    <Eye className="h-3 w-3" />
                                                    View
                                                </button>
                                                {p.status === "complete" ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => p.attempt_id && setReviewing(p.attempt_id)}
                                                        disabled={!p.attempt_id}
                                                        className="inline-flex items-center gap-1.5 text-xs text-emerald-800 bg-emerald-100 border border-emerald-400 hover:bg-emerald-200 disabled:opacity-60 disabled:cursor-not-allowed px-2.5 py-1.5 rounded-md font-semibold transition"
                                                        data-testid={`profile-view-result-${p.id}`}
                                                        title="View completed test"
                                                    >
                                                        <CheckCircle2 className="h-3.5 w-3.5" />
                                                        Reviewed
                                                    </button>
                                                ) : p.status === "in-progress" ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => p.attempt_id && setReviewing(p.attempt_id)}
                                                        disabled={!p.attempt_id}
                                                        className="inline-flex items-center gap-1.5 text-xs text-violet-700 bg-violet-50 border border-violet-200 hover:bg-violet-100 disabled:opacity-60 disabled:cursor-not-allowed px-2.5 py-1.5 rounded-md transition"
                                                        data-testid={`profile-monitor-${p.id}`}
                                                        title="Open the student's in-progress test"
                                                    >
                                                        <Eye className="h-3.5 w-3.5" />
                                                        Open test
                                                    </button>
                                                ) : p.status === "awaiting_review" ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => p.attempt_id && setReviewing(p.attempt_id)}
                                                        disabled={!p.attempt_id}
                                                        className="inline-flex items-center gap-1.5 text-xs text-rose-700 bg-rose-50 border border-rose-200 hover:bg-rose-100 disabled:opacity-60 disabled:cursor-not-allowed px-2.5 py-1.5 rounded-md transition"
                                                        data-testid={`profile-review-${p.id}`}
                                                    >
                                                        <ShieldCheck className="h-3.5 w-3.5" />
                                                        Review
                                                    </button>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => setStartSessionFor(p)}
                                                        className="bg-[#134B30] hover:bg-[#0F3D26] text-white gap-1.5"
                                                        data-testid={`profile-start-${p.id}`}
                                                    >
                                                        <KeyRound className="h-3.5 w-3.5" />
                                                        {p.status === "scheduled" ? "Re-issue" : "Go ahead with test"}
                                                    </Button>
                                                )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="awaiting" className="mt-4">
                    <AttemptsTable items={awaitingReview} loading={loading} onReview={setReviewing} variant="awaiting" />
                </TabsContent>
                <TabsContent value="completed" className="mt-4">
                    <AttemptsTable items={completed} loading={loading} onReview={setReviewing} variant="completed" />
                </TabsContent>
            </Tabs>

            <StartSessionDialog
                profile={startSessionFor}
                onClose={() => { setStartSessionFor(null); reload(); }}
            />
            <ReviewAttemptDialog
                attemptId={reviewing}
                onClose={() => { setReviewing(null); reload(); }}
            />
            <ProfileDetailsDialog
                profile={viewingProfile}
                onClose={() => setViewingProfile(null)}
            />

            <Dialog open={confirmDeleteOpen} onOpenChange={(o) => !o && setConfirmDeleteOpen(false)}>
                <DialogContent className="max-w-md" data-testid="confirm-delete-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Delete selected profiles?</DialogTitle>
                        <DialogDescription>
                            This will permanently remove{" "}
                            <strong>{selectedIds.size}</strong> profile
                            {selectedIds.size === 1 ? "" : "s"}, along with their test
                            sessions, attempts, and any saved answers. This action
                            cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={bulkDelete}
                            disabled={deleting}
                            className="gap-1.5"
                            data-testid="confirm-bulk-delete"
                        >
                            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                            Delete {selectedIds.size} profile{selectedIds.size === 1 ? "" : "s"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ----------------------------------------------------------------------------

function ProfileDetailRow({ label, value }) {
    return (
        <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-border/40 last:border-0">
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold col-span-1">{label}</div>
            <div className="text-sm text-foreground col-span-2 break-words">
                {value || <span className="text-muted-foreground italic text-xs">—</span>}
            </div>
        </div>
    );
}

/**
 * Read-only dialog showing every field the applicant submitted on the public
 * HCC English Test registration form. Helps admins verify identity + contact
 * + demographics before issuing a test ID.
 */
function ProfileDetailsDialog({ profile, onClose }) {
    if (!profile) return null;

    const fullName = profile.full_name || [
        profile.first_name, profile.middle_name, profile.last_name,
    ].filter(Boolean).join(" ");

    const address = [
        profile.address_line,
        profile.address_line_2,
        profile.city,
        profile.province === "Other" ? profile.province_other : profile.province,
        profile.postal_code,
        profile.country,
    ].filter(Boolean).join(", ");

    return (
        <Dialog open={!!profile} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="profile-details-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-[#134B30]">
                        {fullName || profile.email}
                    </DialogTitle>
                    <DialogDescription>
                        Profile submitted on {fmtDate(profile.created_at)} · <StatusBadge status={profile.status} />
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-2">
                    <section>
                        <h3 className="text-xs uppercase tracking-[0.18em] text-[#C79520] font-semibold mb-2">Identity</h3>
                        <ProfileDetailRow label="Full name" value={fullName} />
                        <ProfileDetailRow label="Email" value={profile.email} />
                        <ProfileDetailRow label="Date of birth" value={profile.date_of_birth} />
                        <ProfileDetailRow label="Gender" value={profile.gender} />
                        <ProfileDetailRow label="Enrolled in HS" value={profile.enrolled_in_high_school} />
                    </section>

                    <section>
                        <h3 className="text-xs uppercase tracking-[0.18em] text-[#C79520] font-semibold mb-2">Contact</h3>
                        <ProfileDetailRow label="Address" value={address} />
                        <ProfileDetailRow label="Home phone" value={profile.home_phone} />
                        <ProfileDetailRow label="Mobile phone" value={profile.mobile_phone} />
                    </section>

                    <section>
                        <h3 className="text-xs uppercase tracking-[0.18em] text-[#C79520] font-semibold mb-2">Student identifiers</h3>
                        <ProfileDetailRow label="Student ID" value={profile.student_id} />
                        <ProfileDetailRow
                            label="Confirms"
                            value={profile.confirm_student_id === profile.student_id
                                ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Matches student ID</span>
                                : <span className="inline-flex items-center gap-1 text-rose-700"><XCircle className="h-3.5 w-3.5" /> Mismatch — {profile.confirm_student_id}</span>}
                        />
                        <ProfileDetailRow label="Supplemental ID" value={profile.supplemental_student_id} />
                    </section>

                    {(profile.share_institutions?.length > 0 || profile.opt_out_marketing !== undefined) && (
                        <section>
                            <h3 className="text-xs uppercase tracking-[0.18em] text-[#C79520] font-semibold mb-2">Preferences</h3>
                            <ProfileDetailRow
                                label="Share scores with"
                                value={profile.share_institutions?.length > 0
                                    ? profile.share_institutions.join(", ")
                                    : "—"}
                            />
                            <ProfileDetailRow
                                label="Marketing emails"
                                value={profile.opt_out_marketing
                                    ? <span className="inline-flex items-center gap-1 text-rose-700">Opted out</span>
                                    : <span className="inline-flex items-center gap-1 text-emerald-700">Allowed</span>}
                            />
                        </section>
                    )}

                    {(profile.test_id || profile.test_password) && (
                        <section>
                            <h3 className="text-xs uppercase tracking-[0.18em] text-[#C79520] font-semibold mb-2">Issued test credentials</h3>
                            <ProfileDetailRow label="Test ID" value={profile.test_id} />
                            <ProfileDetailRow label="Password" value={profile.test_password} />
                            <ProfileDetailRow label="Expires" value={fmtDate(profile.test_expires_at)} />
                            <ProfileDetailRow label="Session consumed" value={profile.session_consumed ? "Yes" : "No"} />
                        </section>
                    )}
                </div>

                <DialogFooter className="mt-4">
                    <Button variant="ghost" onClick={onClose} data-testid="profile-details-close">
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ----------------------------------------------------------------------------

function AttemptsTable({ items, loading, onReview, variant }) {
    if (loading) {
        return (
            <Card className="p-6 space-y-3">
                {[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
            </Card>
        );
    }
    if (!items.length) {
        return (
            <Card className="p-12 text-center text-sm text-muted-foreground">
                <ShieldCheck className="h-8 w-8 mx-auto text-[#C79520]/60 mb-2" />
                {variant === "awaiting" ? "No attempts awaiting review." : "No completed attempts yet."}
            </Card>
        );
    }
    return (
        <Card className="overflow-hidden">
            <table className="w-full text-sm" data-testid={`attempts-${variant}`}>
                <thead className="bg-muted/40 text-xs uppercase tracking-[0.14em] text-muted-foreground">
                    <tr>
                        <th className="text-left px-4 py-3 font-semibold">Student</th>
                        <th className="text-left px-4 py-3 font-semibold">Reading</th>
                        <th className="text-left px-4 py-3 font-semibold">Writing</th>
                        <th className="text-left px-4 py-3 font-semibold">Essay</th>
                        <th className="text-left px-4 py-3 font-semibold">Submitted</th>
                        <th className="text-right px-4 py-3 font-semibold">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((a) => (
                        <tr key={a.id} className="border-t border-border" data-testid={`attempt-row-${a.id}`}>
                            <td className="px-4 py-3">
                                <div className="font-medium">{a.student_name}</div>
                                <div className="text-xs text-muted-foreground">{a.student_email}</div>
                            </td>
                            <td className="px-4 py-3 font-mono text-xs">
                                {a.reading_score?.raw_score ?? "—"}/{a.reading_score?.total ?? "—"}
                                <span className="text-muted-foreground"> ({a.reading_score?.percent ?? "—"}%)</span>
                            </td>
                            <td className="px-4 py-3 font-mono text-xs">
                                {a.writing_score?.raw_score ?? "—"}/{a.writing_score?.total ?? "—"}
                                <span className="text-muted-foreground"> ({a.writing_score?.percent ?? "—"}%)</span>
                            </td>
                            <td className="px-4 py-3 font-mono text-xs">
                                {a.essay_overall ? `${a.essay_overall}/8` : "—"}
                            </td>
                            <td className="px-4 py-3 text-xs">{fmtDate(a.submitted_at)}</td>
                            <td className="px-4 py-3 text-right">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => onReview(a.id)}
                                    className="gap-1.5"
                                    data-testid={`attempt-review-${a.id}`}
                                >
                                    <Eye className="h-3.5 w-3.5" />
                                    {variant === "awaiting" ? "Review &amp; grade" : "View"}
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Card>
    );
}

// ----------------------------------------------------------------------------

function StartSessionDialog({ profile, onClose }) {
    const [mode, setMode] = useState("in-person");
    const [creds, setCreds] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showInviteForm, setShowInviteForm] = useState(false);
    const [meetingUrl, setMeetingUrl] = useState("");
    const [inviteNote, setInviteNote] = useState("");
    const [inviteBusy, setInviteBusy] = useState(false);

    useEffect(() => {
        if (profile) {
            setMode(profile.preferred_mode || "in-person");
            setCreds(null);
            setShowInviteForm(false);
            setMeetingUrl("");
            setInviteNote("");
        }
    }, [profile]);

    const generate = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(
                `/admin/accuplacer/profiles/${profile.id}/start-session`,
                { mode },
            );
            setCreds(data);
            if (mode === "online") setShowInviteForm(true);
            toast.success("Test credentials issued.");
        } catch (e) {
            toast.error(fmtError(e, "Could not issue credentials."));
        } finally {
            setBusy(false);
        }
    };

    const sendInvite = async () => {
        if (!meetingUrl.trim()) {
            toast.error("Please paste a Zoom or Meet link.");
            return;
        }
        setInviteBusy(true);
        try {
            await api.post(
                `/admin/accuplacer/sessions/${creds.session_id}/send-invite`,
                { meeting_url: meetingUrl.trim(), note: inviteNote.trim() || null },
            );
            toast.success(`Invitation emailed to ${profile.email}`);
            onClose();
        } catch (e) {
            toast.error(fmtError(e, "Could not send invitation."));
        } finally {
            setInviteBusy(false);
        }
    };

    const copy = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied.");
    };

    if (!profile) return null;

    return (
        <Dialog open={!!profile} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="start-session-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">Issue test credentials</DialogTitle>
                    <DialogDescription>
                        Generate a one-time Test ID + password for{" "}
                        <strong>{profile.full_name}</strong> ({profile.email}). Valid 48 hours.
                    </DialogDescription>
                </DialogHeader>

                {!creds ? (
                    <div className="space-y-3 py-2">
                        <Label>Test mode</Label>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => setMode("in-person")}
                                className={`p-3 rounded-md border-2 text-sm text-left ${
                                    mode === "in-person" ? "border-[#134B30] bg-[#134B30]/5" : "border-border"
                                }`}
                                data-testid="mode-in-person"
                            >
                                <MapPin className="h-4 w-4 mb-1 text-[#134B30]" />
                                <div className="font-medium">In-person on campus</div>
                                <div className="text-xs text-muted-foreground">Show creds to student here</div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode("online")}
                                className={`p-3 rounded-md border-2 text-sm text-left ${
                                    mode === "online" ? "border-[#134B30] bg-[#134B30]/5" : "border-border"
                                }`}
                                data-testid="mode-online"
                            >
                                <Video className="h-4 w-4 mb-1 text-[#134B30]" />
                                <div className="font-medium">Online (Zoom / Meet)</div>
                                <div className="text-xs text-muted-foreground">Email creds + meeting link</div>
                            </button>
                        </div>
                    </div>
                ) : null}

                {creds && !showInviteForm ? (
                    <div className="space-y-4 py-2">
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 flex gap-2">
                            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            <div>
                                <strong>Read these to the student now.</strong> They will not be shown again — copy them somewhere safe.
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Test ID</Label>
                            <div className="flex items-center gap-2">
                                <Input value={creds.test_id} readOnly className="font-mono text-base tracking-widest" data-testid="creds-test-id" />
                                <Button size="icon" variant="outline" onClick={() => copy(creds.test_id)}><Copy className="h-4 w-4" /></Button>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Password</Label>
                            <div className="flex items-center gap-2">
                                <Input value={creds.password} readOnly className="font-mono text-base" data-testid="creds-password" />
                                <Button size="icon" variant="outline" onClick={() => copy(creds.password)}><Copy className="h-4 w-4" /></Button>
                            </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                            Test portal: <code className="bg-muted px-1.5 py-0.5 rounded">{window.location.origin}/accuplacer/test</code>
                            <Button variant="ghost" size="sm" className="ml-2 h-6 text-xs" onClick={() => copy(`${window.location.origin}/accuplacer/test`)}>
                                <Copy className="h-3 w-3" />
                            </Button>
                        </div>
                    </div>
                ) : null}

                {showInviteForm ? (
                    <div className="space-y-3 py-2">
                        <div className="text-[10px] uppercase tracking-wider text-[#C79520]">Email the student</div>
                        <div>
                            <Label>Zoom / Meet link</Label>
                            <Input
                                value={meetingUrl}
                                onChange={(e) => setMeetingUrl(e.target.value)}
                                placeholder="https://zoom.us/j/123456789?pwd=…"
                                data-testid="invite-meeting-url"
                            />
                        </div>
                        <div>
                            <Label>Note for the student (optional)</Label>
                            <Textarea
                                value={inviteNote}
                                onChange={(e) => setInviteNote(e.target.value)}
                                rows={3}
                                placeholder="e.g. Please join 5 minutes early for identity verification."
                                data-testid="invite-note"
                            />
                        </div>
                        <div className="text-xs text-muted-foreground">
                            The Test ID and password will be included automatically in the email.
                        </div>
                    </div>
                ) : null}

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={busy || inviteBusy}>Close</Button>
                    {!creds ? (
                        <Button onClick={generate} disabled={busy} className="bg-[#134B30] hover:bg-[#0F3D26]" data-testid="creds-generate">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                            Generate credentials
                        </Button>
                    ) : showInviteForm ? (
                        <Button onClick={sendInvite} disabled={inviteBusy || !meetingUrl.trim()} className="bg-[#134B30] hover:bg-[#0F3D26] gap-1.5" data-testid="invite-send">
                            {inviteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            Send invitation email
                        </Button>
                    ) : (
                        <Button onClick={onClose} className="bg-[#134B30] hover:bg-[#0F3D26]">Done</Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ----------------------------------------------------------------------------

function ReviewAttemptDialog({ attemptId, onClose }) {
    const [pkt, setPkt] = useState(null);
    const [loading, setLoading] = useState(false);
    const [composeOpen, setComposeOpen] = useState(false);
    const [overrideScore, setOverrideScore] = useState(null);
    const [proctorNotes, setProctorNotes] = useState("");
    const [savingScore, setSavingScore] = useState(false);

    useEffect(() => {
        if (!attemptId) return;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/admin/accuplacer/attempts/${attemptId}`);
                setPkt(data);
                setOverrideScore(data.essay?.final_overall ?? data.essay?.ai_result?.overall ?? null);
                setProctorNotes(data.essay?.proctor_notes || "");
            } catch (e) {
                toast.error(fmtError(e, "Could not load review packet."));
            } finally {
                setLoading(false);
            }
        })();
    }, [attemptId]);

    const saveScore = async () => {
        setSavingScore(true);
        try {
            await api.patch(`/admin/accuplacer/attempts/${attemptId}/essay-score`, {
                overall: overrideScore,
                proctor_notes: proctorNotes,
            });
            toast.success("Score saved.");
        } catch (e) {
            toast.error(fmtError(e, "Could not save score."));
        } finally {
            setSavingScore(false);
        }
    };

    if (!attemptId) return null;

    const { attempt, profile, responses, essay, essay_prompt } = pkt || {};
    const ai = essay?.ai_result;
    const reading = (responses || []).filter((r) => r.section === "reading");
    const writing = (responses || []).filter((r) => r.section === "writing");

    return (
        <>
            <Dialog open={!!attemptId} onOpenChange={(o) => !o && onClose()}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="review-attempt-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Review attempt</DialogTitle>
                        <DialogDescription>
                            Full per-question breakdown + AI essay scoring.
                        </DialogDescription>
                    </DialogHeader>

                    {loading || !pkt ? (
                        <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
                    ) : (
                        <div className="space-y-5">
                            {/* Student card */}
                            <Card className="p-4 bg-muted/30">
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Student</div>
                                        <div className="font-medium">{profile?.full_name}</div>
                                        <div className="text-xs text-muted-foreground">{profile?.email} · {profile?.phone}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Test details</div>
                                        <div className="text-xs">Mode: <strong className="capitalize">{attempt?.mode}</strong></div>
                                        <div className="text-xs">Started: {fmtDate(attempt?.started_at)}</div>
                                        <div className="text-xs">Submitted: {fmtDate(attempt?.submitted_at)}</div>
                                    </div>
                                </div>
                            </Card>

                            {/* Scores summary */}
                            <div className="grid grid-cols-3 gap-3">
                                <Card className="p-4 bg-sky-50 border-sky-200">
                                    <div className="text-[10px] uppercase text-sky-900/70 tracking-wider">Reading</div>
                                    <div className="font-display text-2xl text-sky-900 mt-1">
                                        {attempt?.reading_score?.raw_score}/{attempt?.reading_score?.total}
                                    </div>
                                    <div className="text-xs text-sky-900/70">
                                        {attempt?.reading_score?.percent}% · scaled {attempt?.reading_score?.scaled_200_300}
                                    </div>
                                </Card>
                                <Card className="p-4 bg-violet-50 border-violet-200">
                                    <div className="text-[10px] uppercase text-violet-900/70 tracking-wider">Writing</div>
                                    <div className="font-display text-2xl text-violet-900 mt-1">
                                        {attempt?.writing_score?.raw_score}/{attempt?.writing_score?.total}
                                    </div>
                                    <div className="text-xs text-violet-900/70">
                                        {attempt?.writing_score?.percent}% · scaled {attempt?.writing_score?.scaled_200_300}
                                    </div>
                                </Card>
                                <Card className="p-4 bg-amber-50 border-amber-200">
                                    <div className="text-[10px] uppercase text-amber-900/70 tracking-wider">Essay (final)</div>
                                    <div className="font-display text-2xl text-amber-900 mt-1">
                                        {overrideScore || essay?.ai_result?.overall || "—"}/8
                                    </div>
                                    <div className="text-xs text-amber-900/70">
                                        {essay?.word_count || 0} words · AI {ai?.overall ?? "—"}/8
                                    </div>
                                </Card>
                            </div>

                            {/* MCQ — collapsible per section */}
                            <McqReview title="Reading responses" rows={reading} />
                            <McqReview title="Writing responses" rows={writing} />

                            {/* Essay */}
                            <Card className="p-4 bg-card">
                                <div className="text-[10px] uppercase text-[#C79520] tracking-wider mb-1">Essay</div>
                                <h4 className="font-display text-lg text-[#134B30]">{essay_prompt?.title || "—"}</h4>
                                <div className="bg-[#FBF5E8]/50 border border-[#E1DAC5] rounded-md p-3 mt-2 text-xs whitespace-pre-line">
                                    {essay_prompt?.prompt_text || "—"}
                                </div>
                                <div className="mt-3">
                                    <div className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1">
                                        Student response ({essay?.word_count || 0} words)
                                    </div>
                                    <div className="text-sm whitespace-pre-wrap bg-white border border-border rounded-md p-3 max-h-72 overflow-y-auto font-serif" data-testid="essay-text">
                                        {essay?.essay_text || "—"}
                                    </div>
                                </div>

                                {/* AI breakdown */}
                                {ai ? (
                                    <div className="mt-4 p-3 bg-[#FBF5E8]/40 border border-[#E1DAC5] rounded-md" data-testid="ai-essay-breakdown">
                                        <div className="text-[10px] uppercase text-[#C79520] tracking-wider mb-2">AI breakdown (GPT-5.4)</div>
                                        {ai.scores ? (
                                            <div className="grid grid-cols-4 gap-2 text-xs">
                                                {Object.entries(ai.scores).map(([k, v]) => (
                                                    <div key={k} className="bg-white px-2 py-1.5 rounded border border-[#E1DAC5]">
                                                        <div className="text-[10px] text-muted-foreground capitalize">{k.replace(/_/g, " ")}</div>
                                                        <div className="font-mono font-semibold">{v}/8</div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                        {ai.justification ? (
                                            <p className="text-xs mt-2 italic">{ai.justification}</p>
                                        ) : null}
                                        {(ai.strengths || []).length ? (
                                            <div className="mt-2 text-xs"><strong>Strengths:</strong> {ai.strengths.join("; ")}</div>
                                        ) : null}
                                        {(ai.weaknesses || []).length ? (
                                            <div className="text-xs"><strong>Weaknesses:</strong> {ai.weaknesses.join("; ")}</div>
                                        ) : null}
                                    </div>
                                ) : null}

                                {/* Override */}
                                <div className="grid grid-cols-2 gap-3 mt-4">
                                    <div>
                                        <Label className="text-xs font-semibold">Final essay score (admin)</Label>
                                        <Select value={String(overrideScore || "")} onValueChange={(v) => setOverrideScore(parseInt(v, 10))}>
                                            <SelectTrigger data-testid="essay-override-select"><SelectValue placeholder="Pick 1-8" /></SelectTrigger>
                                            <SelectContent>
                                                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                                                    <SelectItem key={n} value={String(n)}>{n}/8</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex items-end">
                                        <Button onClick={saveScore} disabled={savingScore || !overrideScore} className="bg-[#134B30] hover:bg-[#0F3D26] gap-1.5" data-testid="essay-override-save">
                                            {savingScore ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                                            Save score
                                        </Button>
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <Label className="text-xs font-semibold">Proctor notes (internal)</Label>
                                    <Textarea value={proctorNotes} onChange={(e) => setProctorNotes(e.target.value)} rows={2} data-testid="essay-proctor-notes" />
                                </div>
                            </Card>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="ghost" onClick={onClose}>Close</Button>
                        <Button
                            onClick={async () => {
                                if (!attemptId) return;
                                try {
                                    await api.post(`/admin/accuplacer/attempts/${attemptId}/mark-complete`);
                                    toast.success("Marked as complete.");
                                    onClose();
                                } catch (e) {
                                    toast.error(fmtError(e, "Could not mark complete."));
                                }
                            }}
                            variant="outline"
                            className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 gap-1.5"
                            data-testid="mark-test-complete-btn"
                        >
                            <CheckCircle2 className="h-4 w-4" />
                            Mark test complete
                        </Button>
                        <Button
                            onClick={() => setComposeOpen(true)}
                            disabled={loading}
                            className="bg-[#C79520] hover:bg-[#B17E0F] text-[#0A2818] font-semibold gap-1.5"
                            data-testid="open-send-result"
                        >
                            <Send className="h-4 w-4" />
                            {attempt?.results_sent_at ? "Re-send result email" : "Send result email"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {composeOpen ? (
                <ComposeResultEmail
                    attemptId={attemptId}
                    profile={pkt?.profile}
                    attempt={pkt?.attempt}
                    onClose={() => { setComposeOpen(false); onClose(); }}
                />
            ) : null}
        </>
    );
}

// ----------------------------------------------------------------------------

function McqReview({ title, rows }) {
    const [open, setOpen] = useState(false);
    if (!rows.length) return null;
    const correct = rows.filter((r) => r.is_correct).length;
    return (
        <Card className="p-4">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between text-left"
            >
                <div>
                    <div className="text-[10px] uppercase text-[#C79520] tracking-wider">{title}</div>
                    <div className="font-medium">{correct} correct of {rows.length}</div>
                </div>
                <span className="text-xs text-muted-foreground">{open ? "▲ Hide" : "▼ Show breakdown"}</span>
            </button>
            {open ? (
                <ul className="mt-3 space-y-2">
                    {rows.map((r, i) => (
                        <li key={r.question_id} className={`text-xs border rounded-md p-2 ${
                            r.is_correct ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"
                        }`}>
                            <div className="flex items-center justify-between mb-1">
                                <span className="font-medium">Q{i + 1}</span>
                                <span className={`font-mono text-[11px] ${r.is_correct ? "text-emerald-700" : "text-rose-700"}`}>
                                    {r.is_correct ? <CheckCircle2 className="inline h-3 w-3 mr-1" /> : <XCircle className="inline h-3 w-3 mr-1" />}
                                    {r.selected_option} {r.is_correct ? "✓" : `→ correct ${r.question?.correct_option}`}
                                </span>
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                                {r.question?.question_text || r.question?.sentence}
                            </div>
                        </li>
                    ))}
                </ul>
            ) : null}
        </Card>
    );
}

// ----------------------------------------------------------------------------

function ComposeResultEmail({ attemptId, profile, attempt, onClose }) {
    const defaultBody = `Dear ${profile?.full_name?.split(" ")[0] || "Student"},

Thank you for sitting the HCC English placement test on ${new Date(attempt?.submitted_at || Date.now()).toLocaleDateString()}.

The Office of the Registrar has reviewed your results. Your scores are summarised below; the official placement letter is attached as a PDF.

Reading: ${attempt?.reading_score?.raw_score ?? "—"}/${attempt?.reading_score?.total ?? "—"} (${attempt?.reading_score?.percent ?? "—"}% · scaled ${attempt?.reading_score?.scaled_200_300 ?? "—"})
Writing: ${attempt?.writing_score?.raw_score ?? "—"}/${attempt?.writing_score?.total ?? "—"} (${attempt?.writing_score?.percent ?? "—"}% · scaled ${attempt?.writing_score?.scaled_200_300 ?? "—"})
WritePlacer essay: ${attempt?.essay_overall ?? "—"} / 8

If you have any questions, please reply to this email.

Yours sincerely,
Office of the Registrar
Heritage Community College`;

    const [subject, setSubject] = useState("Your HCC English Test results — Heritage Community College");
    const [body, setBody] = useState(defaultBody);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) { setFile(null); return; }
        if (f.size > 10 * 1024 * 1024) {
            toast.error("Attachment must be 10 MB or smaller.");
            return;
        }
        setFile(f);
    };

    const send = async () => {
        setBusy(true);
        try {
            let attachment_base64 = null;
            let attachment_filename = null;
            if (file) {
                const buf = await file.arrayBuffer();
                attachment_base64 = btoa(
                    new Uint8Array(buf).reduce((s, b) => s + String.fromCharCode(b), ""),
                );
                attachment_filename = file.name;
            }
            await api.post(`/admin/accuplacer/attempts/${attemptId}/send-result`, {
                subject, body, attachment_base64, attachment_filename,
            });
            toast.success(`Result email sent to ${profile.email}`);
            onClose();
        } catch (e) {
            toast.error(fmtError(e, "Could not send email."));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-2xl" data-testid="compose-result-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Compose result email
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-3 py-1">
                    <div className="flex items-center gap-2 border-b border-border pb-2">
                        <Label className="w-14 text-xs text-muted-foreground">To</Label>
                        <Input value={profile.email} readOnly className="border-0 focus-visible:ring-0 bg-transparent p-0 h-7" data-testid="compose-to" />
                    </div>
                    <div className="flex items-center gap-2 border-b border-border pb-2">
                        <Label className="w-14 text-xs text-muted-foreground">Subject</Label>
                        <Input
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="border-0 focus-visible:ring-0 bg-transparent p-0 h-7"
                            data-testid="compose-subject"
                        />
                    </div>
                    <Textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        rows={14}
                        className="border-0 focus-visible:ring-0 resize-none font-serif text-sm leading-relaxed"
                        data-testid="compose-body"
                    />
                    <div className="border-t border-border pt-2">
                        {file ? (
                            <div className="flex items-center gap-2 text-sm bg-muted/30 px-3 py-2 rounded-md">
                                <Paperclip className="h-3.5 w-3.5" />
                                <span className="font-mono text-xs">{file.name}</span>
                                <span className="text-xs text-muted-foreground">({(file.size / 1024).toFixed(0)} KB)</span>
                                <Button variant="ghost" size="sm" className="ml-auto h-6 text-xs" onClick={() => setFile(null)}>Remove</Button>
                            </div>
                        ) : (
                            <label className="inline-flex items-center gap-1.5 cursor-pointer text-sm text-muted-foreground hover:text-foreground" data-testid="compose-attach">
                                <Paperclip className="h-3.5 w-3.5" />
                                Attach PDF
                                <input type="file" accept=".pdf" className="hidden" onChange={onFileChange} />
                            </label>
                        )}
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>Discard</Button>
                    <Button onClick={send} disabled={busy} className="bg-[#134B30] hover:bg-[#0F3D26] gap-1.5" data-testid="compose-send">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        Send
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
